import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ArrowRight, Building2, Loader2 } from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import { api, type Building } from '../lib/api'
import { DashboardLayout } from '../components/DashboardLayout'

/**
 * Step 1 of room creation - Simple form with just name and building
 * After creation, redirects to room edit page for full configuration
 */
export function RoomCreatePage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading: authLoading, checkAuth, refreshAuth } = useAuthStore()
  
  const [name, setName] = useState('')
  const [buildingId, setBuildingId] = useState<string>('')
  const [buildings, setBuildings] = useState<Building[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  
  useEffect(() => {
    checkAuth()
  }, [])
  
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({ to: '/login' })
    }
  }, [authLoading, isAuthenticated, navigate])
  
  useEffect(() => {
    if (isAuthenticated) {
      loadBuildings()
    }
  }, [isAuthenticated])
  
  const loadBuildings = async () => {
    try {
      setIsLoading(true)
      const data = await api.buildings.list()
      setBuildings(data.buildings)
      // Auto-select if there's only one building
      if (data.buildings.length === 1) {
        setBuildingId(data.buildings[0].id)
      }
    } catch (err) {
      // Buildings might not exist yet - that's ok
    } finally {
      setIsLoading(false)
    }
  }
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    if (!name.trim()) {
      setError('Room name is required')
      return
    }
    
    setIsSaving(true)
    
    try {
      const result = await api.rooms.create({
        name: name.trim(),
        buildingId: buildingId || undefined,
      })
      
      // If user was promoted to room_owner, refresh auth
      if (result.promotedToRoomOwner) {
        await refreshAuth()
      }
      
      // Navigate to edit page to complete room setup
      navigate({ to: '/dashboard/rooms/$roomId/edit', params: { roomId: result.room.id } })
    } catch (err: any) {
      setError(err.message || 'Failed to create room')
      setIsSaving(false)
    }
  }
  
  if (authLoading || isLoading) {
    return (
      <DashboardLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      </DashboardLayout>
    )
  }
  
  return (
    <DashboardLayout>
      <div className="max-w-lg mx-auto py-12">
        {/* Progress indicator */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-medium">
              1
            </div>
            <span className="text-sm font-medium text-gray-900">Create</span>
          </div>
          <div className="w-8 h-px bg-gray-300" />
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-sm font-medium">
              2
            </div>
            <span className="text-sm text-gray-500">Configure</span>
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Create a New Room</h1>
            <p className="text-gray-500">Start with the basics, then configure the details</p>
          </div>
          
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Room Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Room Name <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                autoFocus
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
                placeholder="e.g., Conference Room A"
              />
            </div>
            
            {/* Building Dropdown (only show if buildings exist) */}
            {buildings.length > 0 && (
              <div>
                <label htmlFor="building" className="block text-sm font-medium text-gray-700 mb-2">
                  <Building2 className="w-4 h-4 inline-block mr-1" />
                  Building
                </label>
                <select
                  id="building"
                  value={buildingId}
                  onChange={(e) => setBuildingId(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                >
                  <option value="">No building assigned</option>
                  {buildings.map((building) => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  The room will inherit the building's address and opening hours
                </p>
              </div>
            )}
            
            {/* Submit */}
            <button
              type="submit"
              disabled={isSaving || !name.trim()}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-lg transition-colors"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </form>
        </div>
        
        <p className="text-center text-sm text-gray-500 mt-4">
          You can configure images, description, pricing, and availability on the next screen
        </p>
      </div>
    </DashboardLayout>
  )
}
